/**
 * ICONTACTIC cart behaviour (W-129).
 *
 * 1. Keeps the complimentary eBook in the cart: added once there is something paid in the cart, removed when
 *    the cart is otherwise empty, and clamped to a single copy. Verified 2026-09-07 that a $0 line does not
 *    satisfy the minimum quantity on the "Bundle & save" automatic discounts, so the gift cannot pull a
 *    one-item order into a volume tier.
 * 2. Wires the cart upsell: the "Add" button, and the in-place variant picker behind "Choose" (W-158).
 *
 * Loaded once from the cart drawer, which lives in the header on every page, so it survives the section
 * re-renders that replace the cart markup.
 */
(function () {
  var cfg = window.ICT_CART || {};
  if (window.__ictCartReady) return;
  window.__ictCartReady = true;

  var busy = false;

  function readCart() {
    return fetch('/cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
  }

  /** The badge counts what the shopper chose, so the gift line stays out of it. */
  function paidCount(cart) {
    if (!cart) return undefined;
    if (!cfg.giftHandle) return cart.item_count;
    return cart.items.reduce(function (n, item) {
      return item.handle === cfg.giftHandle ? n : n + item.quantity;
    }, 0);
  }

  function announce(cart) {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: { data: { source: 'icontactic-cart', itemCount: paidCount(cart) } },
      })
    );
  }

  function giftLines(cart) {
    var out = [];
    if (!cfg.giftHandle) return out;
    cart.items.forEach(function (item, i) {
      if (item.handle === cfg.giftHandle) out.push({ line: i + 1, quantity: item.quantity });
    });
    return out;
  }

  function change(body) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (r) { return r.json(); });
  }

  function addGift() {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(cfg.giftVariantId), quantity: 1 }] }),
    }).then(function (r) { return r.json(); });
  }

  /** Brings the cart in line with the gift rules. Idempotent: a settled cart makes no request. */
  function reconcile() {
    if (busy || !cfg.giftEnabled || !cfg.giftVariantId || !cfg.giftHandle) return Promise.resolve();
    busy = true;
    return readCart()
      .then(function (cart) {
        var gifts = giftLines(cart);
        var paid = cart.items.length - gifts.length;

        if (paid === 0 && gifts.length > 0) {
          return change({ line: gifts[0].line, quantity: 0 }).then(announce);
        }
        if (paid > 0 && gifts.length === 0) {
          return addGift().then(function () { return readCart(); }).then(announce);
        }
        if (gifts.length > 0 && gifts[0].quantity !== 1) {
          return change({ line: gifts[0].line, quantity: 1 }).then(announce);
        }
        return null;
      })
      .catch(function () { /* a failed reconcile must never block the cart */ })
      .then(function () { busy = false; });
  }

  document.addEventListener('cart:update', function (event) {
    var detail = event.detail || {};
    var data = detail.data || {};
    if (data.source === 'icontactic-cart') return;
    reconcile();
  });

  // W-158: options open inside the cart. "Choose" reveals the variants that are in stock, picking one arms the
  // Add button with that variant id, and nothing navigates away from the cart the shopper already built.
  document.addEventListener('click', function (event) {
    var target = event.target.closest && event.target.closest('[data-ict-upsell-open]');
    if (!target) return;
    event.preventDefault();
    var box = target.closest('.ict-cart-upsell');
    var opts = box && box.querySelector('[data-ict-upsell-opts]');
    if (!opts) return;
    opts.hidden = false;
    target.hidden = true;
    target.setAttribute('aria-expanded', 'true');
    var first = opts.querySelector('[data-ict-variant]');
    if (first) first.focus();
  });

  function upsellVariants(box) {
    var tag = box.querySelector('[data-ict-upsell-variants]');
    if (!tag) return [];
    try { return JSON.parse(tag.textContent) || []; } catch (e) { return []; }
  }

  function chosenOptions(box) {
    var out = [];
    var rows = box.querySelectorAll('[data-ict-opt]');
    for (var i = 0; i < rows.length; i++) {
      var picked = rows[i].querySelector('[aria-checked="true"]');
      out.push(picked ? picked.getAttribute('data-ict-opt-value') : null);
    }
    return out;
  }

  // A value is offerable when at least one available variant carries it alongside everything already picked.
  function refreshUpsell(box) {
    var variants = upsellVariants(box);
    var picked = chosenOptions(box);
    var chips = box.querySelectorAll('[data-ict-opt-value]');
    for (var i = 0; i < chips.length; i++) {
      var chip = chips[i];
      var index = Number(chip.getAttribute('data-ict-opt-index'));
      var value = chip.getAttribute('data-ict-opt-value');
      var reachable = variants.some(function (variant) {
        if (variant.opts[index] !== value) return false;
        return picked.every(function (chosen, at) {
          return at === index || !chosen || variant.opts[at] === chosen;
        });
      });
      chip.disabled = !reachable;
      chip.classList.toggle('is-out', !reachable);
    }

    var complete = picked.every(function (value) { return !!value; });
    var match = complete
      ? variants.filter(function (variant) {
          return variant.opts.every(function (value, at) { return value === picked[at]; });
        })[0]
      : null;

    var confirm = box.querySelector('.ict-cart-upsell__confirm');
    if (confirm) {
      confirm.setAttribute('data-ict-upsell-add', match ? String(match.id) : '');
      confirm.disabled = !match;
    }
    var price = box.querySelector('.ict-cart-upsell__price');
    if (price && match && match.money) price.textContent = match.money;
  }

  document.addEventListener('click', function (event) {
    var chip = event.target.closest && event.target.closest('[data-ict-opt-value]');
    if (!chip || chip.disabled) return;
    event.preventDefault();
    var row = chip.closest('[data-ict-opt]');
    var box = chip.closest('.ict-cart-upsell');
    if (!row || !box) return;
    var siblings = row.querySelectorAll('[data-ict-opt-value]');
    for (var i = 0; i < siblings.length; i++) {
      siblings[i].setAttribute('aria-checked', siblings[i] === chip ? 'true' : 'false');
    }
    refreshUpsell(box);
  });

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('[data-ict-upsell-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    var id = Number(button.getAttribute('data-ict-upsell-add'));
    if (!id) return;
    button.disabled = true;
    button.classList.add('is-loading');
    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: id, quantity: 1 }] }),
    })
      .then(function () { return readCart(); })
      .then(announce)
      .catch(function () { button.disabled = false; button.classList.remove('is-loading'); });
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', reconcile);
  } else {
    reconcile();
  }
})();
